/**
 * Implementação simplificada do Facebook Pixel para SPAs
 * Abordagem defensiva que só usa eventos personalizados
 * Pixel ID é definido por região no regionConfig
 */

import regionConfig from '../config/regionConfig';

// ID do Facebook Pixel (vem da configuração de região)
const PIXEL_ID = regionConfig.facebookPixelId;

// Controle de inicialização
let isInitialized = false;

/**
 * Carrega o script do Facebook Pixel e o inicializa
 * de forma segura sem interferir em seu funcionamento interno
 */
export const initializeFacebookPixel = () => {
    // Evita múltiplas inicializações
    if (isInitialized || typeof window.fbq === 'function') {
        process.env.REACT_APP_ISDEVELOP === 'true' && console.log('Facebook Pixel já está inicializado');
        return;
    }

    // Define a fila de eventos vazia antes de carregar o script
    window.fbq = window.fbq || function() {
        window.fbq.callMethod ? 
            window.fbq.callMethod.apply(window.fbq, arguments) : 
            window.fbq.queue.push(arguments)
    };
    
    window._fbq = window._fbq || window.fbq;
    window.fbq.push = window.fbq;
    window.fbq.loaded = true;
    window.fbq.version = '2.0';
    window.fbq.queue = [];

    // Carrega o script do Facebook assincronamente
    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://connect.facebook.net/en_US/fbevents.js';
    
    script.onload = () => {
        // Inicializa o Pixel SEM o PageView automático
        window.fbq('init', PIXEL_ID, {}, { autoConfig: false });
        process.env.REACT_APP_ISDEVELOP === 'true' && console.log('Facebook Pixel inicializado com sucesso:', PIXEL_ID);
        
        // Marca como inicializado após o carregamento completo
        isInitialized = true;
    };
    
    document.head.appendChild(script);
    process.env.REACT_APP_ISDEVELOP === 'true' && console.log('Script do Facebook Pixel adicionado ao DOM');
};

/**
 * Rastreia mudanças de página como eventos personalizados
 * @param {string} pageName - Nome da página atual
 */
export const trackPageView = (pageName) => {
    if (typeof window.fbq !== 'function') return;
    
    try {
        // Em vez de usar PageView, usamos um evento personalizado
        window.fbq('trackCustom', 'Visualizacao', {
            pagina: pageName,
            timestamp: new Date().toISOString()
        });
        process.env.REACT_APP_ISDEVELOP === 'true' && console.log(`Página rastreada: ${pageName}`);
    } catch (error) {
        process.env.REACT_APP_ISDEVELOP === 'true' && console.error('Erro ao rastrear página:', error);
    }
};

/**
 * Rastreia eventos personalizados
 * @param {string} eventName - Nome do evento
 * @param {Object} params - Parâmetros adicionais
 */
export const trackEvent = (eventName, params = {}) => {
    if (typeof window.fbq !== 'function') return;
    
    try {
        window.fbq('trackCustom', eventName, {
            ...params,
            timestamp: new Date().toISOString()
        });
        process.env.REACT_APP_ISDEVELOP === 'true' && console.log(`Evento rastreado: ${eventName}`);
    } catch (error) {
        process.env.REACT_APP_ISDEVELOP === 'true' && console.error('Erro ao rastrear evento:', error);
    }
};

/**
 * Rastreia eventos padrão do Facebook (Lead, Purchase, CompleteRegistration, etc.)
 * @param {string} eventName - Nome do evento padrão do Facebook
 * @param {Object} params - Parâmetros adicionais
 */
export const trackStandardEvent = (eventName, params = {}) => {
    if (typeof window.fbq !== 'function') return;

    try {
        window.fbq('track', eventName, {
            ...params,
            timestamp: new Date().toISOString()
        });
        process.env.REACT_APP_ISDEVELOP === 'true' && console.log(`Evento padrão rastreado: ${eventName}`);
    } catch (error) {
        process.env.REACT_APP_ISDEVELOP === 'true' && console.error('Erro ao rastrear evento padrão:', error);
    }
};

/**
 * Função legada mantida para compatibilidade
 */
export const reloadFacebookPixel = () => {
    process.env.REACT_APP_ISDEVELOP === 'true' && console.log('Função legada chamada (sem efeito)');
};

